import { defineStyle, defineRecipe } from '@chakra-ui/react';

const baseStyleObject = defineStyle({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'row',
  gap: '2',
  whiteSpace: 'nowrap',
  borderRadius: '8px',
  fontWeight: '600',
  transitionProperty: 'common',
  transitionDuration: 'normal',
  _focusVisible: {
    outline: '2px solid',
    outlineColor: 'border.action',
    outlineOffset: '2px',
  },
  _disabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
    boxShadow: 'none',
  },
  minHeight: '2.5em',
  px: '1em',
  cursor: "pointer"
});

const primaryVariant = defineStyle({
  bg: 'button.primary !important',
  color: 'white',
  _hover: {
    bg: 'button.primaryHover !important',
  },
  _disabled: {
    bg: 'button.primary !important',
  },
  _active: {
    bg: 'button.primaryActive !important',
  },
});

const secondaryVariant = defineStyle({
  bg: 'button.secondary !important',
  color: 'text.primary !important',
  border: '1px solid !important',
  borderColor: 'border.secondary !important',
  _hover: {
    bg: 'surface.secondary !important',
    borderColor: 'border.tertiary !important',
  },
  _disabled: {
    bg: 'button.secondary !important',
    borderColor: 'border.secondary !important',
  },
  _active: {
    bg: 'grayscale.300 !important',
  },
});

const alertVariant = defineStyle({
  bg: 'button.danger !important',
  color: 'white !important',
  border: '1px solid',
  borderColor: 'border.danger',
  _hover: {
    bg: '#C53030 !important',
  },
  _disabled: {
    bg: 'button.danger !important',
  },
  _active: {
    bg: '#9B2C2C !important',
  },
});

const drillVariant = defineStyle({
  bg: 'button.drill !important',
  color: 'white !important',
  border: '1px solid',
  borderColor: 'border.drill',
  _hover: {
    bg: '#2B6CB0 !important',
  },
  _disabled: {
    bg: 'button.drill !important',
  },
  _active: {
    bg: '#2C5282 !important',
  },
});

const reunificationVariant = defineStyle({
  bg: 'button.reunification !important',
  color: 'white !important',
  border: '1px solid',
  borderColor: 'border.reunification',
  _hover: {
    bg: '{colors.purple.600} !important',
  },
  _disabled: {
    bg: 'button.reunification !important',
  },
  _active: {
    bg: '{colors.purple.700} !important',
  },
});

const reportVariant = defineStyle({
  bg: 'button.report !important',
  color: 'white !important',
  border: '1px solid',
  borderColor: 'border.report',
  _hover: {
    bg: '#B7791F !important',
  },
  _disabled: {
    bg: 'button.report !important',
  },
  _active: {
    bg: '#975A16 !important',
  },
});

const successVariant = defineStyle({
  bg: 'button.success !important',
  color: 'white !important',
  border: '1px solid',
  borderColor: 'border.success',
  _hover: {
    bg: '#2F855A !important',
    borderColor: 'border.success',
  },
  _disabled: {
    bg: 'button.success !important',
    borderColor: 'border.success',
  },
  _active: {
    bg: '#276749 !important',
  },
});

const unsafeVariant = defineStyle({
  bg: 'button.unsafe !important',
  color: 'white !important',
  border: '1px solid',
  borderColor: 'border.unsafe',
  _hover: {
    bg: '#9f2714 !important',
    borderColor: 'border.unsafe',
  },
  _disabled: {
    bg: 'button.unsafe !important',
    borderColor: 'border.unsafe',
  },
  _active: {
    bg: '#9f2714 !important',
  },
});

const safeVariant = defineStyle({
  bg: 'button.safe !important',
  color: 'white !important',
  border: '1px solid',
  borderColor: 'border.safe',
  _hover: {
    bg: '#2aa12a !important',
    borderColor: 'border.safe',
  },
  _disabled: {
    bg: 'button.safe !important',
    borderColor: 'border.safe',
  },
  _active: {
    bg: '#2aa12a !important',
  },
});

const clearVariant = defineStyle({
  bg: 'button.clear !important',
  color: 'white !important',
  border: '1px solid',
  borderColor: 'border.clear',
  _hover: {
    bg: '#5a70aa !important',
    borderColor: 'border.clear',
  },
  _disabled: {
    bg: 'button.clear !important',
    borderColor: 'border.clear',
  },
  _active: {
    bg: '#5a70aa !important',
  },
});

const resetVariant = defineStyle({
  bg: 'button.reset !important',
  color: 'white !important',
  border: '1px solid',
  borderColor: 'border.reset',
  _hover: {
    bg: '#7c7b7b !important',
    borderColor: 'border.reset',
  },
  _disabled: {
    bg: 'button.reset !important',
    borderColor: 'border.reset',
  },
  _active: {
    bg: '#7c7b7b !important',
  },
});
const evacuatedVariant = defineStyle({
  bg: 'surface.evacuated !important',
  color: '#1C1C1E !important',
  border: '1px solid',
  borderColor: 'border.evacuated',
  _disabled: {
    bg: 'surface.evacuated !important',
    borderColor: 'border.evacuated',
  },
});

export const buttonRecipe = defineRecipe({
  className: 'chakra-button',
  base: baseStyleObject,
  variants: {
    visual: {
      primary: primaryVariant,
      secondary: secondaryVariant,
      alert: alertVariant,
      drill: drillVariant,
      report: reportVariant,
      reunification: reunificationVariant,
      success: successVariant,
      unsafe: unsafeVariant,
      safe: safeVariant,
      clear: clearVariant,
      reset: resetVariant,
      evacuated: evacuatedVariant,
    },
    size: {
      sm: defineStyle({
        minHeight: '2.3em',
        px: '0.75em',
      }),
      md: defineStyle({
        minHeight: '2.5em',
        px: '1em',
      }),
      lg: defineStyle({
        minHeight: 'controlHeightLg',
        px: '1.5em',
      }),
      fill: defineStyle({
        width: '100%',
        height: '100%',
      }),
      alert: defineStyle({
        width: '100%',
        height: '5em',
        p: '0.67em 0.89em',
      }),
    },
  },
  defaultVariants: {
    visual: 'primary',
    size: 'md',
  },
});
